/**
 * Client entity registry — FULLY AUTO-DERIVED from the server schema.
 *
 * This file never needs manual edits when adding a new entity.
 * Add entities to `entitySchema` in `sync-engine/schema/entities.ts` —
 * everything here updates automatically.
 *
 * Phase B: the unified server schema now contains both UserDo entities
 * (`authority: 'user'`) and StudyGroupDo entities (`authority: 'group'`),
 * with `'both'` reserved for the split-canonical migration (Phase C).
 * This module partitions the schema at runtime so:
 *
 *   - `clientEntities` (and its derived maps) covers only entities that
 *     participate in the user-side store / dispatch. Without this filter
 *     the user's `useFlashkaStore` would auto-generate setters like
 *     `setSgMeta` / `setSgMembers` that point at a store that doesn't
 *     own those tables — a latent runtime bug.
 *
 *   - `clientGroupEntities` covers the StudyGroupDo-side view. It's the
 *     infrastructure that future Phase B continuation (auto-generated
 *     SG slices + registry-driven SG dispatch) will consume.
 *
 * Type-level filtering is deferred: `EntityAuthority` is not preserved
 * as a literal on `SyncEntity`, so `GenericEntityState` below is still
 * a superset that includes the SG slice shapes. The user store's
 * runtime setter set IS filtered, so the superset is harmless until
 * the type-level filter lands in a follow-up.
 */

import type { SyncEntity } from '@flashka/shared/sync-engine/framework/define-sync-entity';
import {
  type EntitySchema,
  entitySchema,
} from '@flashka/shared/sync-engine/schema/entities';

import {
  type ClientSyncEntity,
  createClientEntity,
  type InferSlice,
  type InferSliceFromConfig,
} from './define-sync-entity';

// Re-export so consumers can derive auxiliary slice types if needed.
export type { InferSlice, InferSliceFromConfig };
export type { ClientSyncEntity };

// ---------------------------------------------------------------------------
// Type helpers — derive GenericEntityState from the schema registry
// ---------------------------------------------------------------------------

type UnionToIntersection<U> = (U extends unknown ? (k: U) => void : never) extends (
  k: infer I,
) => void
  ? I
  : never;

/**
 * Derive Zustand slice types from entity schema. Each entity's `.config`
 * carries literal `name`, `type`, `nested`, and `indexById` values
 * inferred from the `defineEntity` call site, so the slice derivation
 * resolves to concrete mapped types (e.g. `{ folders: SyncFolder[] }` or
 * `{ cards: Record<deckId, Record<cardId, SyncCardProps>> }`) instead of
 * index signatures.
 *
 * The `initial` field already carries the COMPUTED state shape (nested
 * for record-with-nested, indexed for collection-with-indexById) via
 * `DefineStateType` — so the slice's value type matches the engine's
 * runtime hydration shape automatically.
 *
 * `GetConfigProp` does a direct `keyof` lookup instead of the `infer X
 * extends Y` pattern on optional properties — under `strictNullChecks:
 * false` (ts-jest), optional-property inference collapses `X` to the
 * constraint type rather than the declared value, mis-routing the
 * slice derivation. Direct indexing preserves literal types like
 * `true` while still resolving absent properties to `undefined`.
 */
type GetConfigProp<C, K extends string, Fallback> = K extends keyof C
  ? C[K] extends Fallback
    ? C[K]
    : Fallback
  : Fallback;

type InferSchemaSlices<S> = UnionToIntersection<
  {
    [K in keyof S]: S[K] extends { config: infer C }
      ? C extends {
          initial: infer State;
          name: infer N extends string;
          type: infer Kind extends 'array' | 'collection' | 'record' | 'value';
        }
        ? InferSliceFromConfig<
            State,
            N,
            Kind,
            GetConfigProp<C, 'nested', boolean> extends true ? true : false,
            GetConfigProp<C, 'indexById', boolean> extends true ? true : false
          >
        : never
      : never;
  }[keyof S]
>;

// ---------------------------------------------------------------------------
// Auto-derived GenericEntityState — no manual InferSlice lines needed.
// Adding a new entity to entitySchema automatically extends this type.
// ---------------------------------------------------------------------------

export type GenericEntityState = InferSchemaSlices<EntitySchema>;

// ---------------------------------------------------------------------------
// Runtime registries — auto-derived from entitySchema, partitioned by authority
// ---------------------------------------------------------------------------

// The schema holds a heterogeneous union of SyncEntity<T>s; when projecting to
// a single list, T is erased. The cast is confined to this boundary.
const allClientEntities: ClientSyncEntity[] = Object.values(entitySchema).map(
  (s) => createClientEntity(s.entity as SyncEntity<unknown>) as ClientSyncEntity,
);

/**
 * User-side view: every entity whose canonical state lives in UserDo
 * (`authority: 'user'`) plus split-canonical entities once they land
 * (`authority: 'both'`). Powers the `useFlashkaStore` slice generation
 * and the per-broadcast dispatch in `sync-message-handlers.ts`.
 */
export const clientEntities: ClientSyncEntity[] = allClientEntities.filter(
  (e) => e.authority === 'user' || e.authority === 'both',
);

/** Map from stateKey → user-side entity (for generic dispatch by stateKey). */
export const clientEntityByStateKey: Record<string, ClientSyncEntity> =
  Object.fromEntries(clientEntities.map((e) => [e.stateKey, e]));

export const clientBroadcastMap: Record<string, ClientSyncEntity> = Object.fromEntries(
  clientEntities.flatMap((e) => {
    const entries: [string, ClientSyncEntity][] = [[e.broadcastType, e]];
    // Register delete broadcast type for record entities
    if (e.deleteBroadcastType && e.applyServerDeleteBroadcast) {
      entries.push([e.deleteBroadcastType, e]);
    }
    // Register bulk wire types for record entities. Since commit 205273b
    // the server's flushLocal / detectDirty emit ONE bulk frame per
    // chunk instead of N per-entry frames, so without these the
    // receiver silently drops every cross-tab fan-out.
    if (e.bulkBroadcastType && e.applyServerBulkBroadcast) {
      entries.push([e.bulkBroadcastType, e]);
    }
    if (e.bulkDeleteBroadcastType && e.applyServerBulkDeleteBroadcast) {
      entries.push([e.bulkDeleteBroadcastType, e]);
    }
    return entries;
  }),
);

// ---------------------------------------------------------------------------
// Group-side registries (Phase B)
//
// Mirrors the user-side registry but filters by `authority === 'group'` or
// `'both'`. Consumed by the future registry-driven SG dispatch (Phase B
// continuation) and by tests that need to enumerate the group view. The
// hand-written `useStudyGroupStore` + `applyIncoming` in
// `use-study-group-do-sync.ts` continue to own runtime today; these
// registries exist so they can be migrated incrementally.
// ---------------------------------------------------------------------------

export const clientGroupEntities: ClientSyncEntity[] = allClientEntities.filter(
  (e) => e.authority === 'group' || e.authority === 'both',
);

/** Map from stateKey → group-side entity (for generic dispatch by stateKey). */
export const clientGroupEntityByStateKey: Record<string, ClientSyncEntity> =
  Object.fromEntries(clientGroupEntities.map((e) => [e.stateKey, e]));

// Per-authority key resolution for split entities — mirrors the same
// helpers in `framework/group-entity-registry.ts`. For `mirror: 'split'`
// entities the group side uses different wire-message names than the
// user side; for everything else the override is undefined and we fall
// back to the top-level `broadcastType` / `deleteBroadcastType`. Without
// this distinction `clientGroupBroadcastMap` would register a split
// entity under its USER-side broadcast type, never reaching the SG
// broadcast that's actually in flight.
function groupBroadcastKey(e: ClientSyncEntity): string {
  return e.mirror === 'split' && e.groupBroadcastType
    ? e.groupBroadcastType
    : e.broadcastType;
}

function groupDeleteBroadcastKey(e: ClientSyncEntity): string | undefined {
  if (e.mirror === 'split' && e.groupDeleteBroadcastType) {
    return e.groupDeleteBroadcastType;
  }
  return e.deleteBroadcastType;
}

function groupBulkBroadcastKey(e: ClientSyncEntity): string | undefined {
  if (e.mirror === 'split' && e.groupBulkBroadcastType) {
    return e.groupBulkBroadcastType;
  }
  return e.bulkBroadcastType;
}

function groupBulkDeleteBroadcastKey(e: ClientSyncEntity): string | undefined {
  if (e.mirror === 'split' && e.groupBulkDeleteBroadcastType) {
    return e.groupBulkDeleteBroadcastType;
  }
  return e.bulkDeleteBroadcastType;
}

/**
 * Map from broadcast message type → group-side entity. Includes both
 * `broadcastType` and (when present) `deleteBroadcastType` so a single
 * lookup covers updates and deletes — mirroring `clientBroadcastMap`.
 *
 * For `mirror: 'split'` entities the keys are the SG-side wire types
 * (e.g. `sg-deckProperties-updated`), not the user-side
 * `deckProperties-updated`. The hand-written SG dispatch in
 * `use-study-group-do-sync.ts` does NOT consume this map yet (Phase B
 * continuation); the map exists so the eventual registry-driven SG
 * dispatch can drop in without an additional rewrite.
 */
export const clientGroupBroadcastMap: Record<string, ClientSyncEntity> =
  Object.fromEntries(
    clientGroupEntities.flatMap((e) => {
      const entries: [string, ClientSyncEntity][] = [[groupBroadcastKey(e), e]];
      const deleteKey = groupDeleteBroadcastKey(e);
      if (deleteKey && e.applyServerDeleteBroadcast) {
        entries.push([deleteKey, e]);
      }
      const bulkKey = groupBulkBroadcastKey(e);
      if (bulkKey && e.applyServerBulkBroadcast) {
        entries.push([bulkKey, e]);
      }
      const bulkDeleteKey = groupBulkDeleteBroadcastKey(e);
      if (bulkDeleteKey && e.applyServerBulkDeleteBroadcast) {
        entries.push([bulkDeleteKey, e]);
      }
      return entries;
    }),
  );
